/**
 * Admin V3 — RefundGuidanceCard (SC-PR-11)
 *
 * Pure presentation sub-component for RefundGuidanceModal. Renders the
 * engine's structured recommendation as a card with classification
 * badge, amount, fee, policy version, and bilingual explanation.
 *
 * INVARIANTS:
 *   - No fetches. The card receives `recommendation` as a prop.
 *   - No mutations / writes / POST surfaces.
 *   - No automatic amount pre-fill anywhere.
 *   - Renders muted "—" / "non disponible" gracefully when recommendation
 *     is null or malformed.
 *
 * Recommendation shape (from GET /admin/bookings/{id}/refund-recommendation,
 * defined by SC-PR-10 RefundRecommendation.to_dict):
 *   {
 *     classification: "FULL_REFUND" | "PARTIAL_REFUND" | "NO_REFUND"
 *                   | "NOT_APPLICABLE" | "MANUAL_REVIEW_REQUIRED",
 *     recommended_amount: string (Decimal-as-string),
 *     recommended_percent: number,
 *     fee_amount: string,
 *     policy_version: string (must contain "draft" + "advisory" per SC-PR-10),
 *     human_readable_explanation_fr: string[],
 *     human_readable_explanation_en: string[],
 *     ...
 *   }
 */

import React from 'react';
import { cn, RecommendationClassBadge } from '../../shared/visualTokens';
import { t } from '../../i18n';

// SC-PR-11A: per-classification accent border so the card is visually
// unmistakable at a glance. Fixes R3 — operator misperception risk
// where the NOT_APPLICABLE card looked too similar to the empty
// placeholder. Each classification gets a 4-px coloured left border
// in the same palette family as its RecommendationClassBadge.
const ACCENT_BORDER_BY_CLASS = {
  FULL_REFUND:            'border-l-4 border-l-emerald-500 border-y border-r border-slate-200',
  PARTIAL_REFUND:         'border-l-4 border-l-amber-500   border-y border-r border-slate-200',
  NO_REFUND:              'border-l-4 border-l-red-500     border-y border-r border-slate-200',
  NOT_APPLICABLE:         'border-l-4 border-l-zinc-400    border-y border-r border-slate-200',
  MANUAL_REVIEW_REQUIRED: 'border-l-4 border-l-purple-500  border-y border-r border-slate-200',
};

const formatAmountFr = (amountStr) => {
  // amountStr arrives as Decimal-string from the backend (e.g. "31.50").
  // Convert to French-locale formatting: "31,50 €".
  const num = Number(amountStr);
  if (Number.isNaN(num)) return '—';
  return `${num.toFixed(2).replace('.', ',')} €`;
};

const classificationLabel = (classification, locale) =>
  t(`bookings.refundGuidance.classification.${classification}`, locale) || classification;

const RefundGuidanceCard = ({ recommendation, locale = 'fr' }) => {
  if (!recommendation) {
    return (
      <div
        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-6 text-center text-[13px] text-slate-500"
        data-testid="adminv3-refund-guidance-card-empty"
      >
        {t('bookings.refundGuidance.state.unavailable', locale)}
      </div>
    );
  }

  const {
    classification,
    recommended_amount,
    recommended_percent,
    fee_amount,
    policy_version,
    human_readable_explanation_fr,
    human_readable_explanation_en,
  } = recommendation;

  const explanations = locale === 'en'
    ? (human_readable_explanation_en || [])
    : (human_readable_explanation_fr || []);

  const showAmounts =
    classification === 'FULL_REFUND' ||
    classification === 'PARTIAL_REFUND' ||
    classification === 'NO_REFUND';

  return (
    <div
      className={cn(
        'rounded-2xl bg-white p-5 shadow-[0_4px_18px_rgba(15,23,42,0.04)]',
        ACCENT_BORDER_BY_CLASS[classification] || 'border border-slate-200'
      )}
      data-testid="adminv3-refund-guidance-card"
      data-classification={classification}
    >
      <div className="flex items-center justify-between mb-4">
        <RecommendationClassBadge
          classification={classification}
          label={classificationLabel(classification, locale)}
        />
        {policy_version ? (
          <span
            className="text-[11px] font-mono text-slate-500"
            data-testid="adminv3-refund-guidance-policy-version"
          >
            {policy_version}
          </span>
        ) : null}
      </div>

      {showAmounts ? (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-400">
              {t('bookings.refundGuidance.card.recommendedAmount', locale)}
            </div>
            <div className="mt-0.5 text-[15px] font-semibold text-slate-900">
              {formatAmountFr(recommended_amount)}
              {typeof recommended_percent === 'number' ? (
                <span className="ml-1 text-[12px] font-normal text-slate-500">
                  ({recommended_percent}%)
                </span>
              ) : null}
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-400">
              {t('bookings.refundGuidance.card.fee', locale)}
            </div>
            <div className="mt-0.5 text-[15px] font-semibold text-slate-900">
              {formatAmountFr(fee_amount)}
            </div>
          </div>
        </div>
      ) : null}

      {explanations.length > 0 ? (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wider text-slate-400">
            {t('bookings.refundGuidance.card.explanation', locale)}
          </div>
          <ul className="list-disc list-inside text-[13px] text-slate-700 leading-relaxed">
            {explanations.map((line, i) => (
              <li key={i}>{line}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export default RefundGuidanceCard;
